/**
 * Shared Admin UI Components Library
 * 
 * Consolidates duplicate styled-components across admin pages into
 * a reusable component library following the Tashi Design System.
 */

import styled from 'styled-components';
import { Link } from 'react-router-dom';

// Re-export complex components
export { ViewModal } from './ViewModal';
export { TemplateBuilder } from './TemplateBuilder';
export { FormBuilder } from './FormBuilder';

// =============================================================================
// LAYOUT COMPONENTS
// =============================================================================

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

export const PageTitle = styled.h1`
  color: var(--ds-text-primary, #2c3e50);
  margin: 0;
  font-size: clamp(22px, 3vw, 28px);
  font-weight: 600;
`;

export const FilterGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

export const Card = styled.div`
  background: var(--ds-surface, white);
  border-radius: var(--radius-lg, 12px);
  padding: 24px;
  box-shadow: var(--shadow-md, 0 2px 8px rgba(0, 0, 0, 0.08));
  margin-bottom: 24px;
`;

export const CardTitle = styled.h2`
  margin: 0 0 20px 0;
  color: var(--ds-text-primary, #2c3e50);
  font-size: 18px;
  font-weight: 600;
`;

export const Section = styled.div`
  border: 1px solid var(--ds-border, #e0e0e0);
  border-radius: var(--radius-md, 8px);
  padding: 20px;
  margin-bottom: 16px;
`;

export const SectionTitle = styled.h3`
  margin: 0 0 16px 0;
  color: var(--ds-text-primary, #34495e);
  font-size: 16px;
  font-weight: 600;
  border-bottom: 1px solid var(--ds-border-light, #e0e0e0);
  padding-bottom: 8px;
`;

// =============================================================================
// BUTTONS
// =============================================================================

export const Button = styled.button<{ 
  $variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'outline';
  $size?: 'sm' | 'md' | 'lg';
}>`
  padding: ${props => {
    switch (props.$size) {
      case 'sm': return '6px 12px';
      case 'lg': return '14px 28px';
      default: return '10px 20px';
    }
  }};
  border-radius: var(--radius-md, 6px);
  border: ${props => props.$variant === 'outline' ? '1px solid var(--ds-primary, #3498db)' : 'none'};
  font-size: ${props => props.$size === 'sm' ? '12px' : '14px'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: ${props => {
    switch (props.$variant) {
      case 'danger': return 'var(--error-color, #e74c3c)';
      case 'secondary': return 'var(--ds-muted, #95a5a6)';
      case 'success': return 'var(--success-color, #27ae60)';
      case 'outline': return 'transparent';
      default: return 'var(--ds-primary, #3498db)';
    }
  }};
  color: ${props => props.$variant === 'outline' ? 'var(--ds-primary, #3498db)' : 'white'};

  &:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

export const IconButton = styled.button`
  padding: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: var(--radius-sm, 4px);
  color: var(--ds-text-secondary, #7f8c8d);
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: var(--ds-surface-muted, #f8f9fa);
    color: var(--ds-text-primary, #2c3e50);
  }
`;

export const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

// =============================================================================
// FORM COMPONENTS
// =============================================================================

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const FormRow3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const Label = styled.label`
  font-weight: 600;
  color: var(--ds-text-primary, #34495e);
  font-size: 14px;
`;

export const Input = styled.input`
  padding: 12px;
  border: 1px solid var(--ds-border, #ddd);
  border-radius: var(--radius-md, 6px);
  font-size: 14px;
  background: var(--ds-surface, white);
  color: var(--ds-text-primary, #2c3e50);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--ds-primary, #3498db);
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
  }

  &::placeholder {
    color: var(--ds-text-muted, #95a5a6);
  }
`;

export const TextArea = styled.textarea`
  padding: 12px;
  border: 1px solid var(--ds-border, #ddd);
  border-radius: var(--radius-md, 6px);
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  background: var(--ds-surface, white);
  color: var(--ds-text-primary, #2c3e50);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--ds-primary, #3498db);
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
  }
`;

export const Select = styled.select`
  padding: 12px;
  border: 1px solid var(--ds-border, #ddd);
  border-radius: var(--radius-md, 6px);
  font-size: 14px;
  background: var(--ds-surface, white);
  color: var(--ds-text-primary, #2c3e50);
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--ds-primary, #3498db);
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
  }
`;

export const SearchInput = styled(Input)`
  padding-left: 40px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2395a5a6'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'%3E%3C/path%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: 12px center;
  background-size: 20px;
`;

// =============================================================================
// TABLE COMPONENTS
// =============================================================================

export const Table = styled.table`
  width: 100%;
  background-color: var(--ds-surface, white);
  border-radius: var(--radius-md, 8px);
  overflow: hidden;
  box-shadow: var(--shadow-sm, 0 2px 4px rgba(0, 0, 0, 0.1));
  border-collapse: collapse;
`;

export const Th = styled.th`
  background-color: var(--ds-primary-dark, #34495e);
  color: white;
  padding: 14px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const Td = styled.td`
  padding: 14px 16px;
  border-bottom: 1px solid var(--ds-border-light, #ecf0f1);
  font-size: 14px;
  color: var(--ds-text-primary, #2c3e50);
`;

export const Tr = styled.tr`
  &:hover {
    background-color: var(--ds-surface-muted, #f8f9fa);
  }

  &:last-child td {
    border-bottom: none;
  }
`;

// =============================================================================
// BADGES & TAGS
// =============================================================================

export const StatusBadge = styled.span<{ 
  $status?: 'active' | 'inactive' | 'pending' | 'archived' | 'success' | 'error' | 'warning';
}>`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: ${props => {
    switch (props.$status) {
      case 'active':
      case 'success': return 'var(--success-light, #d4edda)';
      case 'error': return 'var(--error-light, #f8d7da)';
      case 'warning':
      case 'pending': return 'var(--warning-light, #fff3cd)';
      case 'archived': return 'var(--ds-muted-bg, #e0e0e0)';
      case 'inactive':
      default: return 'var(--ds-muted-bg, #e0e0e0)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'active':
      case 'success': return 'var(--success-color, #155724)';
      case 'error': return 'var(--error-color, #721c24)';
      case 'warning':
      case 'pending': return 'var(--warning-hover, #856404)';
      case 'archived': return 'var(--ds-text-secondary, #5a5a5a)';
      case 'inactive':
      default: return 'var(--ds-text-secondary, #6c757d)';
    }
  }};
`;

export const Tag = styled.span`
  background-color: var(--info-light, #e8f4f8);
  color: var(--info-color, #2980b9);
  padding: 4px 10px;
  border-radius: var(--radius-sm, 4px);
  font-size: 12px;
  font-weight: 500;
`;

export const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

// =============================================================================
// MODAL COMPONENTS
// =============================================================================

export const Modal = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  overflow-y: auto;
  padding: 40px 20px;
`;

export const ModalContent = styled.div`
  background: var(--ds-surface, white);
  border-radius: var(--radius-lg, 12px);
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: var(--shadow-lg, 0 4px 20px rgba(0, 0, 0, 0.15));
`;

export const ModalHeader = styled.div`
  padding: 24px 32px;
  border-bottom: 1px solid var(--ds-border-light, #ecf0f1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background: var(--ds-surface, white);
  z-index: 10;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  color: var(--ds-text-primary, #2c3e50);
  font-size: 20px;
  font-weight: 600;
`;

export const ModalBody = styled.div`
  padding: 24px 32px;
`;

export const ModalFooter = styled.div`
  padding: 16px 32px;
  border-top: 1px solid var(--ds-border-light, #ecf0f1);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  position: sticky;
  bottom: 0;
  background: var(--ds-surface, white);
`;

// =============================================================================
// MESSAGES & ALERTS
// =============================================================================

export const Alert = styled.div<{ $variant?: 'success' | 'error' | 'warning' | 'info' }>`
  padding: 12px 16px;
  border-radius: var(--radius-md, 6px);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 500;
  
  background-color: ${props => {
    switch (props.$variant) {
      case 'success': return 'var(--success-light, #efe)';
      case 'error': return 'var(--error-light, #fee)';
      case 'warning': return 'var(--warning-light, #fff3cd)';
      default: return 'var(--info-light, #e8f4f8)';
    }
  }};
  
  color: ${props => {
    switch (props.$variant) {
      case 'success': return 'var(--success-color, #060)';
      case 'error': return 'var(--error-color, #c00)';
      case 'warning': return 'var(--warning-hover, #856404)';
      default: return 'var(--info-color, #2980b9)';
    }
  }};
  
  border: 1px solid ${props => {
    switch (props.$variant) {
      case 'success': return 'var(--success-color, #27ae60)';
      case 'error': return 'var(--error-color, #e74c3c)';
      case 'warning': return 'var(--warning-color, #f39c12)';
      default: return 'var(--info-color, #3498db)';
    }
  }};
`;

export const ErrorMessage = styled(Alert).attrs({ $variant: 'error' })``;
export const SuccessMessage = styled(Alert).attrs({ $variant: 'success' })``;
export const WarningMessage = styled(Alert).attrs({ $variant: 'warning' })``;
export const InfoMessage = styled(Alert).attrs({ $variant: 'info' })``;

// =============================================================================
// EMPTY STATE & LOADING
// =============================================================================

export const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: var(--ds-text-secondary, #7f8c8d);
  
  h3 {
    margin: 0 0 8px 0;
    color: var(--ds-text-primary, #2c3e50);
    font-size: 18px;
  }
  
  p {
    margin: 0 0 16px 0;
    font-size: 14px;
  }
`;

export const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 40px;
  font-size: 16px;
  color: var(--ds-text-secondary, #7f8c8d);
  
  &::before {
    content: '';
    width: 40px;
    height: 40px;
    border: 3px solid var(--ds-border, #e0e0e0);
    border-top-color: var(--ds-primary, #3498db);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// =============================================================================
// PAGINATION
// =============================================================================

export const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 24px;
`;

export const PageButton = styled.button<{ $active?: boolean }>`
  padding: 8px 14px;
  border: 1px solid var(--ds-border, #ddd);
  background-color: ${props => props.$active ? 'var(--ds-primary, #3498db)' : 'var(--ds-surface, white)'};
  color: ${props => props.$active ? 'white' : 'var(--ds-text-primary, #2c3e50)'};
  border-radius: var(--radius-sm, 4px);
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: ${props => props.$active ? 'var(--ds-primary-dark, #2980b9)' : 'var(--ds-surface-muted, #f8f9fa)'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const PageInfo = styled.span`
  font-size: 14px;
  color: var(--ds-text-secondary, #7f8c8d);
`;

// =============================================================================
// STAT CARDS (Dashboard)
// =============================================================================

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
`;

export const StatCard = styled.div<{ $color?: string }>`
  background: var(--ds-surface, white);
  border-radius: var(--radius-lg, 12px);
  padding: 24px;
  box-shadow: var(--shadow-md, 0 2px 8px rgba(0, 0, 0, 0.08));
  border-left: 4px solid ${props => props.$color || 'var(--ds-primary, #3498db)'};
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg, 0 4px 12px rgba(0, 0, 0, 0.12));
  }
`;

export const StatLabel = styled.div`
  font-size: 13px;
  color: var(--ds-text-secondary, #7f8c8d);
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
`;

export const StatValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: var(--ds-text-primary, #2c3e50);
`;

export const StatTrend = styled.span<{ $positive?: boolean }>`
  font-size: 14px;
  color: ${props => props.$positive !== false ? 'var(--success-color, #27ae60)' : 'var(--error-color, #e74c3c)'};
  margin-left: 12px;
  font-weight: 500;
`;

// =============================================================================
// INFO CARDS (Details View)
// =============================================================================

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
`;

export const InfoCard = styled.div`
  background-color: var(--ds-surface-muted, #f8f9fa);
  padding: 16px;
  border-radius: var(--radius-md, 8px);
  border-left: 4px solid var(--ds-primary, #3498db);
`;

export const InfoLabel = styled.div`
  font-size: 12px;
  color: var(--ds-text-secondary, #7f8c8d);
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 500;
`;

export const InfoValue = styled.div`
  font-size: 14px;
  color: var(--ds-text-primary, #2c3e50);
  word-break: break-word;
`;

// =============================================================================
// TABS
// =============================================================================

export const Tabs = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--ds-border-light, #ecf0f1);
`;

export const Tab = styled.button<{ $active: boolean }>`
  padding: 12px 20px;
  border: none;
  background-color: ${props => props.$active ? 'var(--ds-primary, #3498db)' : 'transparent'};
  color: ${props => props.$active ? 'white' : 'var(--ds-text-secondary, #7f8c8d)'};
  font-weight: 600;
  cursor: pointer;
  border-radius: var(--radius-md, 6px) var(--radius-md, 6px) 0 0;
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover {
    background-color: ${props => props.$active ? 'var(--ds-primary-dark, #2980b9)' : 'var(--ds-surface-muted, #f8f9fa)'};
  }
`;

// =============================================================================
// ACTION BAR (for bulk actions, search, filters)
// =============================================================================

export const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

export const SearchBar = styled.div`
  position: relative;
  flex: 1;
  max-width: 400px;
  min-width: 200px;
`;

export const BulkActionBar = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: var(--info-light, #e8f4f8);
  border-radius: var(--radius-md, 8px);
  margin-bottom: 16px;
`;

// =============================================================================
// CHECKBOX
// =============================================================================

export const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: var(--ds-primary, #3498db);
`;

// =============================================================================
// LINKS
// =============================================================================

export const StyledLink = styled(Link)`
  color: var(--ds-primary, #3498db);
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

// =============================================================================
// QUICK ACTION CARDS
// =============================================================================

export const QuickActionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

export const QuickActionCard = styled(Link)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px;
  background: var(--ds-surface, white);
  border-radius: var(--radius-lg, 12px);
  box-shadow: var(--shadow-sm, 0 2px 4px rgba(0, 0, 0, 0.08));
  text-decoration: none;
  color: var(--ds-text-primary, #2c3e50);
  transition: all 0.2s ease;
  border: 1px solid var(--ds-border-light, #e0e0e0);

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md, 0 4px 8px rgba(0, 0, 0, 0.12));
    border-color: var(--ds-primary, #3498db);
  }
`;

export const QuickActionIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: var(--radius-md, 8px);
  background: var(--ds-primary-light, #e8f4f8);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
`;

export const QuickActionContent = styled.div`
  flex: 1;
  
  h4 {
    margin: 0 0 4px 0;
    font-size: 16px;
    font-weight: 600;
  }
  
  p {
    margin: 0;
    font-size: 13px;
    color: var(--ds-text-secondary, #7f8c8d);
  }
`;
